import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Switch,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Notifications } from '@mui/icons-material';
import styled from 'styled-components';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import TextField from '../../components/common/TextField/TextField';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  getWalletSettings, 
  getWalletInfo,
  updateWalletSettings,
  removeWallet,
  WalletSettings as IWalletSettings,
  WalletInfo
} from '../../utils/api';
import { getCurrentAuthUser } from '../../utils/auth';
import { trackEvent } from '../../utils/analytics';

const SettingCard = styled(Card)`
  margin-bottom: 1.5rem;
`;

const SettingRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }
`;

const SettingLabel = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Settings: React.FC = () => {
  const { walletId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [settings, setSettings] = useState<IWalletSettings | null>(null);
  const [walletInfo, setWalletInfo] = useState<WalletInfo | null>(null);
  const [threshold, setThreshold] = useState<number>(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (walletId) {
      trackEvent('settings_page_view', {
        wallet_id: walletId
      });
    }
  }, [walletId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!walletId) {
        setError('Invalid wallet ID');
        return;
      }
      
      try {
        setLoading(true);
        
        // Get current user
        const user = await getCurrentAuthUser();
        if (!user?.userId) {
          navigate('/auth');
          return;
        }

        const [settingsData, infoData] = await Promise.all([
          getWalletSettings(walletId, user.userId),
          getWalletInfo(walletId, user.userId)
        ]);
        
        setSettings(settingsData);
        setWalletInfo(infoData);
        trackEvent('settings_loaded', {
          wallet_id: walletId,
          enabled: settingsData.enabled,
          threshold: settingsData.threshold
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load wallet information';
        console.error('Error fetching wallet data:', error);
        setError(errorMessage);
        trackEvent('settings_load_error', {
          wallet_id: walletId,
          error: errorMessage
        });
        setTimeout(() => {
          navigate('/wallets');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [walletId, navigate]);

  const handleToggle = async () => {
    if (!walletId || !settings) return;
    
    try {
      setUpdating(true);
      const user = await getCurrentAuthUser();
      if (!user?.userId) {
        navigate('/auth');
        return;
      }
      const response = await updateWalletSettings(walletId, user.userId, { enabled: !settings.enabled });
      if (response.status === 'success') {
        setSettings((prev: IWalletSettings | null) => {
          if (!prev) return null;
          return {
            ...prev,
            ...response.updates
          };
        });
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        trackEvent('settings_toggle', {
          wallet_id: walletId,
          enabled: !settings.enabled
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update settings';
      setError(errorMessage);
      trackEvent('settings_toggle_error', {
        wallet_id: walletId,
        error: errorMessage
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleThresholdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (value >= 1) {
      setSettings((prev: IWalletSettings | null) => {
        if (!prev) return null;
        return {
          ...prev,
          threshold: value
        };
      });
      setError('');
    } else {
      setError('Threshold must be at least 1 XCH');
    }
  };

  const handleSave = async () => {
    if (!walletId || !settings) return;
    
    try {
      setUpdating(true);
      const user = await getCurrentAuthUser();
      if (!user?.userId) {
        navigate('/auth');
        return;
      }
      const response = await updateWalletSettings(walletId, user.userId, { threshold: settings.threshold });
      if (response.status === 'success') {
        setSettings((prev: IWalletSettings | null) => {
          if (!prev) return null;
          return {
            ...prev,
            ...response.updates
          };
        });
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        trackEvent('settings_threshold_update', {
          wallet_id: walletId,
          threshold: settings.threshold
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update settings';
      setError(errorMessage);
      trackEvent('settings_threshold_error', {
        wallet_id: walletId,
        error: errorMessage
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteWallet = async () => {
    if (!walletId) {
      setError(t('settings.messages.error.generic'));
      return;
    }
    
    if (window.confirm(t('settings.messages.deleteConfirm'))) {
      try {
        setUpdating(true);
        console.log('Attempting to delete wallet:', walletId);
        
        const user = await getCurrentAuthUser();
        if (!user?.userId) {
          navigate('/auth');
          return;
        }

        const response = await removeWallet(Number(walletId), user.userId);
        
        console.log('Delete wallet response:', response);
        
        if (response.status === 'success') {
          setSuccess(true);
          setTimeout(() => {
            navigate('/wallets');
          }, 1500);
          trackEvent('wallet_deleted', {
            wallet_id: walletId
          });
        } else {
          throw new Error(t('settings.messages.error.delete'));
        }
      } catch (err) {
        console.error('Error deleting wallet:', err);
        const errorMessage = err instanceof Error ? err.message : t('settings.messages.error.delete');
        setError(errorMessage);
        trackEvent('wallet_delete_error', {
          wallet_id: walletId,
          error: errorMessage
        });
      } finally {
        setUpdating(false);
      }
    }
  };

  if (loading && !settings?.walletId) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Typography variant="h4" gutterBottom>
          {t('settings.title')}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" paragraph>
          {t('settings.subtitle')}
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('settings.messages.success')}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <SettingCard title={t('settings.notifications.title')}>
              <SettingRow>
                <SettingLabel>
                  <Notifications />
                  <Box>
                    <Typography variant="subtitle1">
                      {t('settings.notifications.emailNotifications')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('settings.notifications.emailDescription')}
                    </Typography>
                  </Box>
                </SettingLabel>
                <Switch
                  checked={settings?.enabled}
                  onChange={handleToggle}
                  disabled={loading}
                />
              </SettingRow>

              <SettingRow>
                <SettingLabel>
                  <Box>
                    <Typography variant="subtitle1">
                      {t('settings.notifications.threshold.title')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('settings.notifications.threshold.description')}
                    </Typography>
                  </Box>
                </SettingLabel>
                <TextField
                  type="number"
                  value={settings?.threshold}
                  onChange={handleThresholdChange}
                  inputProps={{ min: 1, step: "any" }}
                  sx={{ width: 120 }}
                  disabled={loading}
                />
              </SettingRow>
            </SettingCard>

            <Box mt={4}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : t('settings.actions.save')}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <SettingCard title={t('settings.walletInfo.title')}>
              <Typography
                variant="subtitle2"
                sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 3 }}
              >
                {walletInfo?.masterKey}
              </Typography>

              <Button 
                variant="outlined" 
                color="error" 
                fullWidth 
                onClick={handleDeleteWallet}
                disabled={loading}
              >
                {t('settings.actions.delete')}
              </Button>
            </SettingCard>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Settings; 